import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = anon, obj = user
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    // Skip if returning from OAuth callback
    if (typeof window !== "undefined" && window.location.hash.includes("session_id=")) {
      setChecking(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("eatup_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.token) localStorage.setItem("eatup_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const startPhoneLogin = async (phone) => {
    const { data } = await api.post("/auth/phone/start", { phone });
    return data; // { challenge_id, phone, dev_mode }
  };

  const verifyPhoneLogin = async ({ challenge_id, code, name }) => {
    const { data } = await api.post("/auth/phone/verify", { challenge_id, code, name });
    if (data.token) localStorage.setItem("eatup_token", data.token);
    setUser(data.user);
    return data; // { user, token, is_new_user }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("eatup_token");
    setUser(null);
  };

  const startGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, checking, login, register, logout, refresh, refreshUser: refresh, startGoogleLogin, startPhoneLogin, verifyPhoneLogin }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
